
import React, { useState } from 'react';
import { Palette, Globe, Check, Monitor, Moon, Sun, GitBranch, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePreferences } from '../../hooks/usePreferences';
import Select from '../../components/ui/Select';

const PreferencesPage = () => {

  const { preferences, updatePreference } = usePreferences();
  const { t, i18n } = useTranslation();
  const [saveIndicator, setSaveIndicator] = useState('');

  const handlePreferenceChange = async (key, value) => {
    // Optimistic update handles local UI, we just await the DB change to show the toast
    await updatePreference(key, value);

    if (key === 'language' && i18n.language !== value) {
      i18n.changeLanguage(value);
    }

    setSaveIndicator(t('messages.saved_successfully'));
    setTimeout(() => setSaveIndicator(''), 3000);
  };

  const handleExportColumnToggle = async (columnKey) => {
    const currentColumns = preferences.export_default_columns || [];
    let updatedColumns;
    
    if (currentColumns.includes(columnKey)) {
      updatedColumns = currentColumns.filter((col) => col !== columnKey);
    } else {
      updatedColumns = [...currentColumns, columnKey];
    }
    
    console.log(`📊 Updated export columns:`, updatedColumns);
    await updatePreference('export_default_columns', updatedColumns);
    setSaveIndicator(t('messages.saved_successfully'));
    setTimeout(() => setSaveIndicator(''), 3000);
  };

  // Safe defaults
  const theme = preferences.theme || 'system';
  const language = preferences.language || 'en';
  const dateFormat = preferences.date_format || 'DD/MM/YYYY';

  // Workflow defaults
  const defaultLandingPage = preferences.defaultLandingPage || '/dashboard';
  const exportDefaultColumns = preferences.export_default_columns || [];

  // Options for Localization
  const languageOptions = [
    { label: 'English', value: 'en' },
    { label: 'Bahasa Melayu', value: 'ms' },
    { label: 'Japanese (日本語)', value: 'ja' },
  ];

  const dateFormatOptions = [
    { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
    { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
    { label: 'YYYY-MM-DD (ISO)', value: 'YYYY-MM-DD' },
  ];

  const landingPageOptions = [
    { label: 'Dashboard', value: '/dashboard' },
    { label: 'Asset List', value: '/asset-list' },
    { label: 'Assignment Management', value: '/checkout-management' },
    { label: 'Lifecycle Planning', value: '/lifecycle-planning' },
  ];

  // Export column options - must match keys used in csvExport.js
  const exportColumnOptions = [
    { key: 'asset_tag', label: t('preferences.export_columns.asset_tag') },
    { key: 'product_name', label: t('preferences.export_columns.product_name') },
    { key: 'category', label: t('preferences.export_columns.category') },
    { key: 'status', label: t('preferences.export_columns.status') },
    { key: 'location', label: t('preferences.export_columns.location') },
    { key: 'department', label: t('preferences.export_columns.department') },
    { key: 'supplier', label: t('preferences.export_columns.supplier') },
    { key: 'purchase_date', label: t('preferences.export_columns.purchase_date') },
    { key: 'purchase_price', label: t('preferences.export_columns.purchase_price') },
    { key: 'serial_number', label: t('preferences.export_columns.serial_number') },
  ];

  return (
    <div className="relative animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">{t('sidebar.preferences')}</h1>
        <p className="text-muted-foreground mt-1">{t('preferences.subtitle')}</p>
      </div>
      {/* Toast Indicator */}
      <div 
        className={`fixed bottom-8 right-8 flex items-center gap-2 px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-lg shadow-xl transition-all duration-300 z-50 ${
          saveIndicator ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <Check size={16} className="text-green-400" />
        {saveIndicator}
      </div>
      <div className="space-y-8">
        {/* Appearance Settings */}
        <div className="bg-card rounded-2xl border border-border shadow-sm">
          <div className="px-8 py-6 border-b border-border flex items-center gap-3">
            <div className="p-2 bg-primary/10 text-primary rounded-lg">
              <Palette size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{t('preferences.appearance')}</h2>
              <p className="text-sm text-muted-foreground">{t('preferences.appearance_desc')}</p>
            </div>
          </div>
          
          <div className="p-8 space-y-8">
            {/* Theme Segmented Control */}
            <div>
              <label className="text-sm font-semibold text-foreground block mb-3">{t('preferences.theme')}</label>
              <div className="flex gap-4">
                <label className="flex-1 cursor-pointer">
                  <input 
                    type="radio" 
                    name="theme" 
                    className="peer sr-only" 
                    checked={theme === 'light'} 
                    onChange={() => handlePreferenceChange('theme', 'light')} 
                  />
                  <div className="flex flex-col items-center justify-center py-4 px-3 border-2 border-border rounded-xl peer-checked:border-blue-600 peer-checked:bg-blue-50 hover:bg-background transition-all">
                    <Sun size={24} className={theme === 'light' ? 'text-primary' : 'text-muted-foreground/70'} />
                    <span className={`mt-2 text-sm font-medium ${theme === 'light' ? 'text-primary' : 'text-muted-foreground'}`}>{t('preferences.light')}</span>
                  </div>
                </label>

                <label className="flex-1 cursor-pointer">
                  <input 
                    type="radio" 
                    name="theme" 
                    className="peer sr-only" 
                    checked={theme === 'dark'} 
                    onChange={() => handlePreferenceChange('theme', 'dark')} 
                  />
                  <div className="flex flex-col items-center justify-center py-4 px-3 border-2 border-border rounded-xl peer-checked:border-blue-600 peer-checked:bg-blue-50 hover:bg-background transition-all">
                    <Moon size={24} className={theme === 'dark' ? 'text-primary' : 'text-muted-foreground/70'} />
                    <span className={`mt-2 text-sm font-medium ${theme === 'dark' ? 'text-primary' : 'text-muted-foreground'}`}>{t('preferences.dark')}</span>
                  </div>
                </label>

                <label className="flex-1 cursor-pointer">
                  <input 
                    type="radio" 
                    name="theme" 
                    className="peer sr-only" 
                    checked={theme === 'system'} 
                    onChange={() => handlePreferenceChange('theme', 'system')} 
                  />
                  <div className="flex flex-col items-center justify-center py-4 px-3 border-2 border-border rounded-xl peer-checked:border-blue-600 peer-checked:bg-blue-50 hover:bg-background transition-all">
                    <Monitor size={24} className={theme === 'system' ? 'text-primary' : 'text-muted-foreground/70'} />
                    <span className={`mt-2 text-sm font-medium ${theme === 'system' ? 'text-primary' : 'text-muted-foreground'}`}>{t('preferences.system_default')}</span>
                  </div>
                </label>
              </div>
            </div>


          </div>
        </div>

        {/* Localization Settings */}
        <div className="bg-card rounded-2xl border border-border shadow-sm">
          <div className="px-8 py-6 border-b border-border flex items-center gap-3">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <Globe size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{t('preferences.localization')}</h2>
              <p className="text-sm text-muted-foreground">{t('preferences.localization_desc')}</p>
            </div>
          </div>
          
          <div className="p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label={t('preferences.interface_language')}
                description={t('preferences.reload_notice')}
                options={languageOptions}
                value={language}
                onChange={(val) => handlePreferenceChange('language', val)}
              />
              
              <Select
                label={t('preferences.date_format')}
                description={t('preferences.date_format_desc')}
                options={dateFormatOptions}
                value={dateFormat}
                onChange={(val) => handlePreferenceChange('date_format', val)}
              />
            </div>
          </div>
        </div>

        {/* Workflow Settings */}
        <div className="bg-card rounded-2xl border border-border shadow-sm">
          <div className="px-8 py-6 border-b border-border flex items-center gap-3">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <GitBranch size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{t('preferences.workflow')}</h2>
              <p className="text-sm text-muted-foreground">{t('preferences.workflow_desc')}</p>
            </div>
          </div>
          
          <div className="p-8 space-y-8 max-w-2xl">
            
            <Select
              label={t('preferences.default_landing_page')}
              description={t('preferences.default_landing_page_desc')}
              options={landingPageOptions}
              value={defaultLandingPage}
              onChange={(val) => handlePreferenceChange('defaultLandingPage', val)}
            />

            {/* Export Columns Grid */}
            <div>
              <label className="text-sm font-semibold text-foreground block mb-4">
                {t('preferences.export_columns_title')}
              </label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {exportColumnOptions.map((column) => (
                  <label
                    key={column.key}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={exportDefaultColumns.includes(column.key)}
                      onChange={() => handleExportColumnToggle(column.key)}
                      className="w-4 h-4 text-blue-600 rounded border-border bg-background cursor-pointer"
                    />
                    <span className="text-sm font-medium text-foreground">{column.label}</span>
                  </label>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-900">
                  <strong>{t('preferences.tip')}:</strong> {t('preferences.export_columns_tip')}
                </p>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default PreferencesPage;
